import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
} from "@mui/material";
import {
  isMeetingDeleteConfirmText,
  MEETING_DELETE_CONFIRM_WORD,
} from "../../utils/meetingRecorder";

export default function MeetingDeleteDialog({
  open,
  session,
  deleting,
  onClose,
  onConfirm,
}) {
  const { t } = useTranslation();
  const theme = useTheme();
  const isNarrow = useMediaQuery(theme.breakpoints.down("sm"));
  const [confirmText, setConfirmText] = useState("");

  useEffect(() => {
    if (open) setConfirmText("");
  }, [open, session?.id]);

  const canConfirm = isMeetingDeleteConfirmText(confirmText) && !deleting;
  const title = session?.title || t("Meetings.untitled");

  return (
    <Dialog
      open={open}
      onClose={() => !deleting && onClose()}
      fullWidth
      fullScreen={isNarrow}
      maxWidth="xs"
    >
      <DialogTitle>{t("Meetings.deleteTitle")}</DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ mb: 2 }}>
          {t("Meetings.deleteWarning", { title })}
        </DialogContentText>
        <DialogContentText sx={{ mb: 1.5, fontWeight: 600 }}>
          {t("Meetings.deleteTypePrompt", {
            word: MEETING_DELETE_CONFIRM_WORD,
          })}
        </DialogContentText>
        <TextField
          autoFocus
          fullWidth
          margin="dense"
          value={confirmText}
          onChange={(e) => setConfirmText(e.target.value)}
          placeholder={MEETING_DELETE_CONFIRM_WORD}
          disabled={deleting}
          inputProps={{
            "aria-label": t("Meetings.deleteTypePrompt", {
              word: MEETING_DELETE_CONFIRM_WORD,
            }),
            autoComplete: "off",
          }}
        />
      </DialogContent>
      <DialogActions
        sx={{
          px: 2,
          py: 1.5,
          flexDirection: { xs: "column-reverse", sm: "row" },
          gap: 1,
          "& > :not(style)": { m: 0 },
        }}
      >
        <Button fullWidth={isNarrow} onClick={onClose} disabled={deleting}>
          {t("Meetings.cancel")}
        </Button>
        <Button
          fullWidth={isNarrow}
          color="error"
          variant="contained"
          disabled={!canConfirm}
          onClick={onConfirm}
          sx={{ minHeight: 48 }}
        >
          {deleting ? (
            <CircularProgress size={18} color="inherit" />
          ) : (
            t("Meetings.deleteConfirm")
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
